import { FloatingFormItem } from '@/components/_common';
import { SpedDocumentStatus } from '@/core/domain/sped/sped-documents/sped-documents.types';
import { Row, Col, Form, Input, Table, Button, Modal } from 'antd';
import {
  useSpedDocumentContext,
  useSpedDocumentHasItemContext,
} from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import {
  useSpedDocumentManageImports,
  useSpedDocumentPurchases,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import { useState, useCallback, useEffect } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import {
  InvoiceSpedStatusRegistered,
  InvoiceSpedStatusRegisteredConstants,
} from '@/core/domain/invoice/invoices/invoices.enums';
import StatusTag from '@/components/_common/StatusTag';
import { useDebounce } from '@/hooks';

interface FormValues {
  key: string;
  documentNumber: string;
  code: number;
  supplierName: string;
}

export default function SpedDocumentPurchases() {
  const spedDocument = useSpedDocumentContext();
  const [form] = Form.useForm<FormValues>();

  const hasRemovePermission = hasPermissions([
    Authorities.SPED_DOCUMENT_IMPORT_PURCHASE,
  ]);

  const formValues = useDebounce(Form.useWatch([], form), 1000);

  const statusEnabled = [
    SpedDocumentStatus.AWAITING_SELECTION,
    SpedDocumentStatus.ASSESSING,
  ];

  const removeDisabled =
    (!!spedDocument?.status && !statusEnabled.includes(spedDocument?.status)) ||
    !hasRemovePermission;

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const { data: purchases, dataUpdatedAt } = useSpedDocumentPurchases({
    ...formValues,
    spedDocumentId: spedDocument?.id as number,
  });

  const [, handleHasItem] = useSpedDocumentHasItemContext();

  useEffect(() => {
    if (
      purchases?.some((item) =>
        [
          InvoiceSpedStatusRegistered.PENDING,
          InvoiceSpedStatusRegistered.ERROR,
        ].includes(item.statusRegisteredInPlugStorage),
      )
    ) {
      handleHasItem(true);
    }
  }, [purchases, dataUpdatedAt]);

  const spedDocumentManageImports = useSpedDocumentManageImports();

  const removeConfirm = useCallback(async () => {
    Modal.confirm({
      title: 'Remoção de compras importadas',
      content: `Tem certeza que deseja remover as "${idsSelected.length}" seleções de compras importadas? essa ação não poderá ser revertida`,
      okText: `Remover "${idsSelected.length}"`,
      onOk: async () => {
        await spedDocumentManageImports.mutateAsync({
          importType: 'REMOVE',
          spedDocumentId: spedDocument?.id as number,
          items: idsSelected.map((id) => ({
            entityId: id,
            entityEnum: 'PURCHASE',
          })),
        });
        setIdsSelected([]);
      },
      okButtonProps: { danger: true },
    });
  }, [idsSelected]);

  return (
    <>
      <Form form={form}>
        <Row style={{ marginBottom: 24 }} gutter={[16, 16]}>
          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="code" label="cód. compra">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="key" label="chave do XML">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="documentNumber" label="N° do documento">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={4} md={6}>
            <FloatingFormItem name="supplierName" label="Nome do fornecedor">
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <Table
        dataSource={purchases || []}
        rowKey={(item) => item.id}
        rowSelection={{
          selectedRowKeys: idsSelected,
          onChange: (keys) => setIdsSelected(keys as number[]),
          getCheckboxProps: (props) => {
            const disabled =
              props.statusRegisteredInPlugStorage ===
                InvoiceSpedStatusRegistered.INSERTED || removeDisabled;
            return { disabled };
          },
        }}
        pagination={false}
        columns={[
          { title: 'Código', dataIndex: 'code' },
          { title: 'Chave do XML', dataIndex: 'key' },
          { title: 'Nº Documento', dataIndex: 'documentNumber' },
          {
            title: 'Fornecedor',
            dataIndex: 'supplierName',
          },
          {
            title: 'Status',
            render: (_, item) => {
              const info =
                InvoiceSpedStatusRegisteredConstants[
                  item.statusRegisteredInPlugStorage
                ];

              return <StatusTag color={info.color} label={info.label} />;
            },
          },
        ]}
        scroll={{ x: true }}
      />

      <div style={{ marginTop: 24 }}>
        <Button
          type="primary"
          danger
          disabled={idsSelected.length < 1 || removeDisabled}
          onClick={removeConfirm}
        >
          {idsSelected.length > 0
            ? `Remover "${idsSelected.length}" ${
                idsSelected.length > 1 ? 'seleções' : 'seleção'
              }`
            : 'Remover seleção'}
        </Button>
      </div>
    </>
  );
}
